// src/components/MMVideoList.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";

import { db, auth } from "../api/firebase";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";

import { AppThemeContext } from "./AppShell";
import { useAuth } from "../context/auth";

// YouTube 用
const extractYouTubeId = (url) => {
  const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|[^/\n\s]+\/\S*?[^\w\s-]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const cleanYouTubeUrl = (url) => {
  const id = extractYouTubeId(url);
  return id ? `https://www.youtube.com/embed/${id}` : null;
};

// Vimeo 用
const extractVimeoId = (url) => {
  const regex = /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:video\/)?(\d+)(?:\/\S*)?/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const extractVimeoHash = (url) => {
  const regex = /vimeo\.com\/\d+\/([a-zA-Z0-9]+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

export default function MMVideoList() {
  const themeFromAppShell = useContext(AppThemeContext) || {};
  const accent = themeFromAppShell.railActive || "#E0A800";
  const surface = themeFromAppShell.surfaceBg || "#FFFFFF";
  const border = themeFromAppShell.bottomBorder || "rgba(0,0,0,0.06)";
  const textBase = themeFromAppShell.railText || "rgba(40,40,60,0.9)";

  const { user } = useAuth();

  const [videos, setVideos] = useState([]);
  const [userPayments, setUserPayments] = useState([]);
  const [selected, setSelected] = useState(null);

  // 🔑 ユーザーの payments 取得（allowedTerm フィルタ用）
  useEffect(() => {
    if (!user) return;
    const fetchUserPayments = async () => {
      try {
        const ref = doc(db, "users", auth.currentUser.uid);
        const snap = await getDoc(ref);
        if (!snap.exists()) return;

        const payments = snap.data().payments || [];
        const paymentPhases =
          payments?.map((p) => String(p.paymentPhase)) || [];
        setUserPayments(paymentPhases);
      } catch (e) {
        console.error("failed to load payments:", e);
      }
    };
    fetchUserPayments();
  }, [user]);

  // 🎥 videos コレクションを購読（閲覧専用）
  useEffect(() => {
    const q = query(collection(db, "videos"), orderBy("order", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setVideos(list);
    });
    return () => unsub();
  }, []);

  // 🌱 ユーザーの payment に合う動画だけ残す
  const visibleVideos = useMemo(() => {
    if (!userPayments.length) return [];
    return videos.filter((video) => {
      const allowed = video.allowedTerm?.map(String) || [];
      if (!allowed.length) return true; // allowedTerm 未設定なら全員可
      return allowed.some((term) => userPayments.includes(term));
    });
  }, [videos, userPayments]);

  const handleOpen = (video) => setSelected(video);
  const handleClose = () => setSelected(null);

  const formatDate = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : ts;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}/${m}/${day}`;
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden", // ★ ここで横スクロールを抑止
      }}
    >
      {/* <Typography
        variant="h6"
        sx={{
          fontWeight: 800,
          color: textBase,
          mb: 1.5,
        }}
      >
        動画
      </Typography> */}

      <Stack spacing={1.5} sx={{ width: "100%", maxWidth: "100%" }}>
        {visibleVideos.map((video) => (
          <Card
            key={video.id}
            onClick={() => handleOpen(video)}
            sx={{
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              borderRadius: 3,
              //   p: { xs: 1.5, sm: 2 },
              bgcolor: "rgba(255,255,255,0.95)",
              border: `1px solid ${border}`,
              boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
              cursor: "pointer",
              "&:hover": {
                boxShadow: "0 14px 30px rgba(0,0,0,0.10)",
                transform: "translateY(-1px)",
              },
              transition: "all .18s ease-out",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "row", sm: "row" },
                alignItems: "center", // ★ 常に中央揃えにしたほうが綺麗
                gap: { xs: 1.5, sm: 2 },
                width: "100%",
                maxWidth: "100%",
              }}
            >
              {/* サムネイル */}
              <Box
                sx={{
                  flexShrink: 0,
                  width: { xs: 96, sm: 120 },
                  height: { xs: 72, sm: 80 },
                  borderRadius: 3,
                  background:
                    "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                    `linear-gradient(135deg, ${accent}22, ${accent}11)`,
                  boxShadow:
                    "0 10px 25px rgba(0,0,0,0.10), inset 0 0 0 1px rgba(255,255,255,0.9)",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundImage: (() => {
                    const thumb = getYouTubeThumbnail(video.url);
                    return thumb
                      ? `url(${thumb})`
                      : "linear-gradient(135deg, #00000010, #00000005)";
                  })(),
                }}
              >
                {!getYouTubeThumbnail(video.url) && (
                  <Box
                    sx={{
                      width: { xs: 42, sm: 46 },
                      height: { xs: 42, sm: 46 },
                      borderRadius: "50%",
                      bgcolor: accent,
                      boxShadow:
                        "0 10px 24px rgba(0,0,0,0.25), 0 0 0 4px rgba(255,255,255,0.9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box>
                      <PlayArrowRoundedIcon
                        sx={{ fontSize: 28, color: "#fff", ml: 0.4 }}
                      />
                    </Box>
                  </Box>
                )}
                {/* 日付をサムネ下部に */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 6,
                    left: 0,
                    right: 0,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1,
                      py: 0.2,
                      borderRadius: 999,
                      bgcolor: "rgba(255,255,255,0.88)",
                      color: textBase,
                      fontSize: 11,
                    }}
                  >
                    {formatDate(video.createdAt)}
                  </Typography>
                </Box>
              </Box>

              {/* タイトル + 説明 */}
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  width: "100%",
                  maxWidth: "100%",
                }}
              >
                {/* タイトル */}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: textBase,
                    mb: 0.5,
                    fontSize: 14,
                    lineHeight: 1.4,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    // ★ ここを追加
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {video.title}
                </Typography>

                {/* 説明 */}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 12,
                    color: "rgba(0,0,0,0.55)",
                    lineHeight: 1.6,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    // ★ 同じく追加（URL対策）
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {video.description}
                </Typography>
              </Box>
            </Box>
          </Card>
        ))}
      </Stack>

      {/* 🎬 動画再生ダイアログ */}
      <Dialog
        open={Boolean(selected)}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            bgcolor: surface,
            borderRadius: { xs: 3, sm: 4 },
            m: { xs: 1, sm: 2 },
            width: "100%",
            boxSizing: "border-box",
          },
        }}
      >
        {selected && (
          <>
            <DialogTitle
              sx={{
                pr: 5,
                fontWeight: 700,
                color: textBase,
                fontSize: 16,
              }}
            >
              {selected.title}
              <IconButton
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                pt: 1,
                pb: 2,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: "100%",
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: "0 16px 40px rgba(0,0,0,0.25)",
                  mb: 2,
                  // 16:9
                  "&::before": {
                    content: '""',
                    display: "block",
                    paddingTop: "56.25%",
                  },
                }}
              >
                {(() => {
                  const url = selected.url || "";
                  if (url.includes("vimeo.com")) {
                    const id = extractVimeoId(url);
                    const h = extractVimeoHash(url);
                    const src = `https://player.vimeo.com/video/${id}${
                      h ? `?h=${h}` : ""
                    }`;
                    return (
                      <iframe
                        src={src}
                        title={selected.title}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        style={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          border: 0,
                        }}
                      />
                    );
                  }
                  if (url.includes("youtube.com") || url.includes("youtu.be")) {
                    const src = cleanYouTubeUrl(url);
                    return (
                      <iframe
                        src={src}
                        title={selected.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          border: 0,
                        }}
                      />
                    );
                  }
                  // その他 iframe
                  return (
                    <iframe
                      src={url}
                      title={selected.title}
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        border: 0,
                      }}
                    />
                  );
                })()}
              </Box>

              {selected.description && (
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", color: textBase }}
                >
                  {selected.description}
                </Typography>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}

const getYouTubeThumbnail = (url) => {
  const id = extractYouTubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};
